<template>
  <div style="width: 100%;">
    <div class="align-items-end">
      <wiskGrid v-if="configAvailable" :showFoundArchived="showFoundArchived" v-bind="$attrs" :gridStyle="gridAutoHeight ? null : gridStyle" @gridApi="gridReady" :resetRows="resetRows" :searchQuery="itemFilterQuery || searchQuery"
        :gridOptions="gridOptions" :columnDefs="columnDefs" :rowData="localItems" :parentGridName="parentGridName || $parent.$options.name" :loadingOverlay="loadingOverlay" :infoTooltipBaseName="infoTooltipBaseName"
        @selectedRowsChanged="setSelectedBottleIds" @visibleRows="setVisibleRows" :groupsCollapsed="groupsCollapsed" :key="gridKey" :groupCheckboxSelection="groupCheckboxSelection"
        @searchQueryClear="localItemIdFilter = null; $emit('searchQueryClear')" :defaultFilter="defaultFilter" :customFilter="gridCustomFilter" :header="header"
        :searchLabel="searchLabel || translations.txtGenericBottleSearchPlaceholder" ref="wiskGrid" :searchFieldGeneratorParams="{ propertiesToExclude: ['distributor', 'title_for_search', 'country', 'categories'] }"
        @requestFilter="$emit('requestFilter', $event)" :gridAutoHeight="gridAutoHeight" :selectedRowsActions="selectedRowsActions"
        :doesExternalFilterPassOverride="doesExternalFilterPassOverride" :filterSystemViews="filterSystemViews" @displayedColumnsChanged="updateOrderGuideColumnGrouped">

        <template v-slot:additional-header-controls>
          <slot name="additional-controls"></slot>
        </template>

        <template v-slot:search-right>
          <slot name="search-right"></slot>
        </template>

      </wiskGrid>
    </div>

    <confirm ref="confirmDialog" />

    <wiskModal v-model="copyToVenueModal" :title="translations.txtGenericCopyToVenues" :okDisabled="!venuesToCopyItem?.length" @ok="copyItemToVenue"
      :okText="translations.txtGenericSubmit" @hide="venuesToCopyItem = null">
      <wiskSelect :items="siblingVenues" :label="translations.txtGenericCopyToVenues" @change="venuesToCopyItem = $event" fixedWrapper multiple :allowAddNewItem="false" />
      <wiskInput infoTooltipKey="90262437-9972-40f2-a100-4135f02548ae" :label="translations.txtGenericCopyToVenueOnlyMatchBasedOnIds" v-model="onlyMatchBasedOnIds" inputType="checkbox" class="mb-1" />
      <wiskInput infoTooltipKey="dca96252-1bee-48bf-8506-f1690e809676" :label="translations.txtGenericCopyToVenueKeepDistributorsOnItems" v-model="keepDistributorsOnItems" inputType="checkbox" />
    </wiskModal>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import * as Sentry from '@sentry/vue'
import get from 'lodash.get'
import merge from 'lodash.merge'
import { mapActions, mapGetters, mapState } from 'vuex'
import api from '@/api'
import { getBaseValueFromMeasurement, guid } from '@/modules/utils'
import getItemsGridColdefs from '@/components/grids/options/itemsGridColDefs'
import { ownBatchWithoutRealTimeStock } from '@/modules/batchItems'
import wiskGrid from '@/components/grids/WiskGrid'

export default {
  name: 'WiskItemsGrid',
  emits: ['selectedRowsChanged', 'gridApi', 'requestFilter', 'searchQueryClear'],
  components: { wiskGrid },
  props: {
    gridStyle: {
      type: Object,
      default: () => ({ height: 'calc(100vh - 130px)' })
    },
    searchQuery: String,
    doesExternalFilterPassOverride: Function,
    itemIdFilter: Number,
    additionalHeaderCols: { type: Number, default: 3 },
    additionalHeaderColsMobile: { type: Number, default: 6 },
    ignoreRowSelection: Boolean,
    gridAutoHeight: Boolean,
    reactToChangesFast: Boolean,
    header: Object,
    options: { type: Object, default: () => ({}) }, //TODO: rename to gridOptions
    items: Array,
    columns: Object,
    searchLabel: String,
    preventCustomFieldsAsColumns: Boolean,
    loading: Boolean,
    parentGridName: { type: String, required: true },
    infoTooltipBaseName: String,
    defaultFilter: {
      type: Object,
      validator: value => value && typeof value.predicate === 'function',
      default: () => ({ predicate: item => !item.archived && !item.grouped_to && !get(item, 'variant.archived'), name: 'active', label: 'Active' })
    },
    customFilter: {
      type: Object,
      validator: value => value && typeof value.predicate === 'function'
    },
    selectedRowsActionsOverride: Array,
    resetRows: Boolean,
    showFoundArchived: Boolean,
    itemsByVariation: Boolean,
    groupCheckboxSelection: { type: Boolean, default: true },
    centralStockroom: Boolean
  },
  data() {
    return {
      orderGuideColumnGrouped: false,
      gridApi: null,
      gridOptions: null,
      gridKey: 1,
      unMounted: false,
      debug: 0,
      localItems: [],
      visibleRows: [],
      loadingOverlay: false,
      columnDefs: [],
      localItemIdFilter: null,
      store: this.$store,
      groupsCollapsed: true,
      selectedBottles: [],
      createColomDefsTimeoutId: null,
      isSAQselected: false,
      forceRefreshAllTimeoutId: null,
      prepareLocalItemsTimeoutId: null,
      configAvailable: false,
      onDemandStateIdStockPrediction: guid(),
      onDemandStateIdStockPredictionFromSales: guid(),
      copyToVenueModal: false,
      venuesToCopyItem: null,
      onlyMatchBasedOnIds: false,
      keepDistributorsOnItems: false,
      copyToVenueRows: []
    }
  },
  computed: {
    ...mapGetters(['countries', 'orderFormats', 'venue', 'activeDistributors', 'activeFamilies', 'upgradeNowTextComputed', 'activeLocations', 'countriesByCode', 'activeAllergens', 'siblingVenues', 'distributorLinkedToCentralStockroom', 'hasCentralStockroom', 'activeOrderGuideLists', 'orderGuideVariantIdsByListId', 'defaultOrderGuideVariantIdsMap', 'orderGuideListTitlesByVariantId']),
    ...mapState([
      'user',
      'orderGuideVariantIdsMap',
      'activeItemIds',
      'customFieldsByTarget',
      'customFieldsByTargetActive',
      'currentPlanFeaturesByType',
      'shoppingCartByItemId',
      'allergensById',
      'distributorsById',
      'categoriesById',
      'currentPermissionsByType',
      'familiesById',
      'distributorsById',
      'parLevelsById',
      'translations',
      'itemPricesById',
      'itemVariantPricesById',
      'bottlesById',
      'bottles',
      'bottleStockPredictionsFromSalesById',
      'bottlesStockPredictionsById',
      'bottleAlgosById',
      'itemVariantsById',
      'locationsById',
      'posIntegrations',
      'categories'
    ]),
    activeItems() {
      //this is a hack, bottles and bottlesById should be the only truth
      return this.venue.parent_venue_id ? this.activeItemIds.map(id => this.bottlesById[id] || { item_id: id }) : this.bottles
    },
    activeBottles() {
      return this.activeItems.filter(b => b && !b.archived)
    },
    itemFilterQuery() {
      if (this.localItemIdFilter) {
        let bottle = this.bottlesById[this.localItemIdFilter] || {}
        return bottle.title || ''
      }
      return ''
    },
    // The order guide the "order guide item" checkbox column works on: the one picked in the order guide filter
    // (Prepare Orders), otherwise the default order guide.
    orderGuideColumnList() {
      let filterName = get(this.customFilter, 'name') || '',
        orderGuideListId = filterName.startsWith('order_guide_list_') ? filterName.replace('order_guide_list_', '') : null

      return (
        (orderGuideListId && this.activeOrderGuideLists.find(orderGuideList => `${orderGuideList.id}` === orderGuideListId)) ||
        this.activeOrderGuideLists.find(orderGuideList => orderGuideList.is_default) ||
        null
      )
    },
    // When the grid is grouped by the order guide checkbox column ("In Order Guide" view), an order guide filter only
    // picks the guide the checkboxes work on: items not in it stay visible ("Not in order guide" group), so they can be added.
    gridCustomFilter() {
      if (this.orderGuideColumnGrouped && (get(this.customFilter, 'name') || '').startsWith('order_guide')) {
        return { ...this.customFilter, predicate: item => !item.archived }
      }
      return this.customFilter
    },
    orderGuideColumnVariantIdsMap() {
      return this.orderGuideColumnList ? this.orderGuideVariantIdsByListId[this.orderGuideColumnList.id] || {} : this.defaultOrderGuideVariantIdsMap
    },
    // Name of the order guide the checkbox column works on, shown in its header and group labels.
    // Empty when the venue has a single order guide: there is nothing to tell apart.
    orderGuideColumnListTitle() {
      return this.activeOrderGuideLists.length > 1 && this.orderGuideColumnList ? this.orderGuideColumnList.display_title : ''
    },
    customFilterCombined() {
      if (this.localItemIdFilter) {
        let copy = { ...this.customFilter }
        copy.predicate = item => item.item_id === this.localItemIdFilter && this.customFilter.predicate(item)

        return copy
      }
      return this.customFilter
    },
    defaultFilterCombined() {
      if (this.localItemIdFilter) {
        let copy = { ...this.defaultFilter }
        copy.predicate = item => item.item_id === this.localItemIdFilter && this.defaultFilter.predicate(item)

        return copy
      }
      return this.defaultFilter
    },
    hasPos() {
      return this.posIntegrations.filter(i => !i.is_archived).length > 0
    },
    priceFee() {
      return this.venue && this.venue.price_fee
    },
    priceRetail() {
      return this.venue && this.venue.price_retail
    },
    selectedItemsMeasurementsMatch() {
      return [...new Set(this.selectedBottles.filter(i => i && i.measurement).map(item => item.measurement.type))].length === 1
    },
    selectedItemsMeasurementsMatchStrict() {
      return [...new Set(this.selectedBottles.filter(i => i && i.measurement).map(item => item.measurement.type + getBaseValueFromMeasurement(item.measurement)))].length === 1
    },
    showingArchived() {
      return get(this.customFilter, 'name') === 'archived'
    },
    excludeFromVarianceVisible() {
      for (let i = 0; i < this.selectedBottles.length; i++) {
        if (!this.selectedBottles[i].excluded_from_variance) {
          return true
        }
      }

      return false
    },
    variantIdAvailable() {
      if (this.selectedBottles && this.selectedBottles[0]) {
        return !!this.selectedBottles[0].item_distributor_id
      }

      return false
    },
    orderGuideFound() {
      for (let i = 0; i < this.selectedBottles.length; i++) {
        if (this.selectedBottles[i].order_guide_item || (this.selectedBottles[i].item_distributor_id && this.orderGuideVariantIdsMap[this.selectedBottles[i].item_distributor_id])) {
          return true
        }
      }

      return false
    },
    includeInVarianceVisible() {
      for (let i = 0; i < this.selectedBottles.length; i++) {
        if (this.selectedBottles[i].excluded_from_variance) {
          return true
        }
      }
      return false
    },
    selectedRowsActions() {
      let actions = [],
        selectedItemsMeasurementsMatchStrict = this.selectedItemsMeasurementsMatchStrict,
        orderGuideFound = this.orderGuideFound,
        multipleOrderGuideLists = this.activeOrderGuideLists.length > 1,
        variantIdAvailable = this.variantIdAvailable,
        selectedItemsMeasurementsMatch = this.selectedItemsMeasurementsMatch,
        showingArchived = this.showingArchived,
        includeInVarianceVisible = this.includeInVarianceVisible,
        excludeFromVarianceVisible = this.excludeFromVarianceVisible,
        someItemsLocked = this.selectedBottles.some(i => i.is_measurement_locked)

      if (!this.ignoreRowSelection && this.selectedBottles && this.selectedBottles.length) {
        if (Array.isArray(this.selectedRowsActionsOverride)) {
          return this.selectedRowsActionsOverride
        }

        actions = [
          {
            key: 'merge',
            type: 'button',
            variant: 'primary',
            disabled: !selectedItemsMeasurementsMatchStrict || !this.selectedBottles || this.selectedBottles.length < 2 || this.selectedBottles.length > 5,
            action: rows => {
              this.mergeItems(rows, false)
            },
            tooltip: !selectedItemsMeasurementsMatchStrict ? this.translations.txtBottlesMergeVolumeValidationMessage : (this.selectedBottles.length < 2 || this.selectedBottles.length > 5 ? this.translations.txtBottlesMergeRangeSelectedMessage : ''),
            title: this.translations.txtWiskItemsFloatingActionsMerge
          },
          {
            key: 'group',
            type: 'button',
            variant: 'primary',
            disabled: this.itemsByVariation || !selectedItemsMeasurementsMatch || !this.selectedBottles || this.selectedBottles.length < 2,
            action: rows => {
              this.mergeItems(rows, true)
            },
            tooltip: this.itemsByVariation ? this.translations.txtBottlesVariantGroupDisabled : !selectedItemsMeasurementsMatch ? this.translations.txtBottlesGroupVolumeValidationMessage : (this.selectedBottles.length < 2 ? this.translations.txtBottlesGroupRangeSelectedMessage : ''),
            title: this.translations.txtWiskItemsFloatingActionsGroup
          },
          {
            key: 'categorize',
            type: 'button',
            variant: 'primary',
            hide: !this.user.god_mode,
            action: rows => {
              this.categorizeItems(rows)
            },
            title: this.translations.txtWiskItemsCategorize
          },
          {
            key: 'archive',
            type: 'button',
            variant: 'danger',
            hide: showingArchived || !this.currentPermissionsByType.item_archive,
            action: rows => {
              if (this.itemsByVariation) {
                this.updateItemVariants({ type: 'archive', value: true }, rows)
              } else {
                this.updateBottles({ type: 'archive', value: true }, rows)
              }
            },
            title: this.translations.txtGenericArchive
          },
          {
            // With several order guides the user picks which one to add to / remove from
            key: 'order_guide_list_add',
            type: 'genericInput',
            hide: !multipleOrderGuideLists,
            inputTypeAttrs: {
              operation: 'order_guide_list_id',
              inputType: 'wiskSelect',
              items: this.activeOrderGuideLists,
              titleKey: 'display_title',
              clearAfterEmitOperation: true,
              label: this.translations.txtOrderGuideListsAddTo
            },
            operation: (operation, rows) => {
              this.updateOrderGuideList(true, operation, rows)
            }
          },
          {
            key: 'order_guide_list_remove',
            type: 'genericInput',
            hide: !multipleOrderGuideLists || !orderGuideFound,
            inputTypeAttrs: {
              operation: 'order_guide_list_id',
              inputType: 'wiskSelect',
              items: this.activeOrderGuideLists,
              titleKey: 'display_title',
              clearAfterEmitOperation: true,
              label: this.translations.txtOrderGuideListsRemoveFrom
            },
            operation: (operation, rows) => {
              this.updateOrderGuideList(false, operation, rows)
            }
          },
          {
            key: 'order_guide_set',
            type: 'button',
            variant: 'outline-primary',
            hide: multipleOrderGuideLists,
            action: rows => {
              if (variantIdAvailable) {
                this.updateItemVariants({ type: 'order_guide', value: true }, rows)
              } else {
                this.updateBottles({ type: 'order_guide', value: true }, rows)
              }
            },
            title: this.translations.txtCartBuilderOrderGuideItemsSet
          },
          {
            key: 'order_guide_clear',
            type: 'button',
            variant: 'outline-danger',
            hide: multipleOrderGuideLists || !orderGuideFound,
            action: rows => {
              if (variantIdAvailable) {
                this.updateItemVariants({ type: 'order_guide', value: false }, rows)
              } else {
                this.updateBottles({ type: 'order_guide', value: false }, rows)
              }
            },
            title: this.translations.txtCartBuilderOrderGuideItemsClear
          },
          {
            key: 'restore',
            type: 'button',
            variant: 'success',
            hide: !showingArchived,
            action: rows => {
              if (this.itemsByVariation) {
                this.updateItemVariants({ type: 'archive', value: false }, rows)
              } else {
                this.updateBottles({ type: 'archive', value: false }, rows)
              }
            },
            title: this.translations.txtGenericRestore
          },
          {
            key: 'exclude_from_variance',
            type: 'button',
            variant: 'outline-primary',
            hide: !excludeFromVarianceVisible,
            action: rows => {
              this.updateBottles({ type: 'excluded_from_variance', value: true }, rows)
            },
            title: this.translations.txtGenericExcludeFromVariance
          },
          {
            key: 'include_in_variance',
            type: 'button',
            variant: 'outline-primary',
            hide: !includeInVarianceVisible,
            action: rows => {
              this.updateBottles({ type: 'excluded_from_variance', value: false }, rows)
            },
            title: this.translations.txtGenericIncludeInVariance
          },
          {
            key: 'category_id',
            type: 'genericInput',
            inputTypeAttrs: {
              operation: 'category_id',
              inputType: 'wiskSelect',
              items: this.activeFamilies,
              clearAfterEmitOperation: true,
              label: this.translations.txtGenericCategory,
              multiselectOptions: {
                groupValues: 'categories',
                groupLabel: 'title',
                multiple: false
              }
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'copy_to_venues',
            type: 'button',
            variant: 'primary',
            icon: 'wisk-copy',
            title: this.translations.txtGenericCopyToVenues,
            hide: !this.currentPermissionsByType.venue_data_copy,
            action: (rows) => {
              this.copyToVenueRows = rows
              this.copyToVenueModal = true
            }
          },
          {
            key: 'country_code',
            type: 'genericInput',
            inputTypeAttrs: {
              trackBy: 'code',
              operation: 'country',
              inputType: 'wiskSelect',
              items: this.countries,
              clearAfterEmitOperation: true,
              label: this.translations.txtGenericCountry
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'distributor_id',
            type: 'genericInput',
            inputTypeAttrs: {
              operation: 'distributor_id',
              operationEmpty: 'distributor_clear',
              inputType: 'wiskSelect',
              items: this.activeDistributors,
              clearAfterEmitOperation: true,
              label: this.translations.txtGenericDistributor
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'case_size',
            type: 'genericInput',
            inputTypeAttrs: {
              operation: 'case_size',
              inputType: 'number',
              value: -1111111,
              clearAfterEmitOperation: true,
              disabled: someItemsLocked,
              tooltip: someItemsLocked ? this.translations.txtBottlesMeasurementLockedInfo : '',
              label: this.translations.txtGenericCaseSize
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'par_level',
            type: 'genericInput',
            inputTypeAttrs: {
              operation: 'par_level',
              inputType: 'number',
              value: -1111111,
              decimals: 2,
              clearAfterEmitOperation: true,
              label: this.translations.txtGenericParLevel
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'clearParLevel',
            type: 'button',
            variant: 'outline-danger',
            action: rows => {
              this.updateBottles({ type: 'par_level', value: 0 }, rows)
            },
            title: this.translations.txtWiskItemsClearParLevel
          },
          {
            key: 'order_format',
            type: 'genericInput',
            inputTypeAttrs: {
              operation: 'order_format',
              inputType: 'wiskSelect',
              items: this.orderFormats,
              clearAfterEmitOperation: true,
              label: this.translations.txtGenericOrderFormat
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'measurement',
            type: 'genericInput',
            hide: this.selectedBottles.length > 10,
            inputTypeAttrs: {
              preferredType: get(this.selectedBottles, '0.measurement.type'),
              disableTypeChange: true,
              disabled: someItemsLocked,
              tooltip: someItemsLocked ? this.translations.txtBottlesMeasurementLockedInfo : '',
              inputType: 'measurement',
              inputClass: 'measurement-component-container-in-grid-actions',
              clearAfterEmitOperation: true
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'inventory_area_add',
            type: 'genericInput',
            inputTypeAttrs: {
              operation: 'inventory_area_add',
              operationEmpty: 'inventory_area_delete',
              inputType: 'wiskSelect',
              items: this.activeLocations,
              taggable: true,
              multiple: true,
              valueName: 'inventory_areas',
              clearAfterEmitOperation: true,
              label: this.translations.txtMenuLocations
            },
            operation: (operation, rows) => {
              this.updateBottlesQuick(operation, rows)
            }
          },
          {
            key: 'active_in_venue_set',
            type: 'genericInput',
            hide: !this.venue.child_venues?.length,
            inputTypeAttrs: {
              operation: 'active_in_venue_set',
              inputType: 'gridMultiSelect',
              parentGridName: 'ItemAlwaysActivInGrid',
              items: this.venue.child_venues,
              valueName: 'always_active_in',
              label: this.translations.txtParentVenueItemsActiveInVenues,
              titleLabel: this.translations.txtGenericVenue,
              useButtonForSave: true,
              clearAfterEmitOperation: true
            },
            operation: (operation, rows) => {
              this.updateBottlesQuick(operation, rows)
            }
          },
          {
            key: 'inventory_alert',
            type: 'genericInput',
            hide: !this.hasPos,
            inputTypeAttrs: {
              operation: 'inventory_alert',
              inputType: 'number',
              value: -1111111,
              clearAfterEmitOperation: true,
              label: this.translations.txtGenericInventoryAlert
            },
            operation: (operation, rows) => {
              this.updateBottles(operation, rows)
            }
          },
          {
            key: 'allergen',
            type: 'genericInput',
            hide: this.venue.venue_type !== 'food',
            inputTypeAttrs: {
              operation: 'allergen_add',
              operationEmpty: 'allergen_delete',
              inputType: 'wiskSelect',
              items: this.activeAllergens,
              taggable: true,
              multiple: true,
              valueName: 'allergens',
              clearAfterEmitOperation: true,
              label: this.translations.txtAllergens
            },
            operation: (operation, rows) => {
              this.updateBottlesQuick(operation, rows)
            }
          }
        ]

        if (this.customFieldsByTargetActive && this.customFieldsByTargetActive.item && this.customFieldsByTargetActive.item.length) {
          this.customFieldsByTargetActive.item.forEach((customField, index) => {
            actions.push({
              key: 'customField_' + index,
              type: 'genericInput',
              inputTypeAttrs: { ...customField.inputTypeAttrs, clearAfterEmitOperation: true },
              operation: (operation, rows) => {
                this.updateBottles(operation, rows)
              }
            })
          })
        }
      }
      return actions
    }
  },
  methods: {
    ...mapActions(['updateBottle', 'setGlobalAction', 'setItemVariant', 'notify', 'setOnDemandState']),
    setSelectedBottleIds(rows) {
      if (!this.ignoreRowSelection) {
        this.selectedBottles = [...rows]
      }
      this.$emit('selectedRowsChanged', rows)
    },
    gridReady(agGridApi) {
      this.gridApi = agGridApi
      this.updateOrderGuideColumnGrouped()

      this.$emit('gridApi', agGridApi)
    },
    updateOrderGuideColumnGrouped() {
      let columnState = this.gridApi ? this.gridApi.getColumnState() : []
      this.orderGuideColumnGrouped = columnState.some(column => column.colId === 'orderGuide' && column.rowGroup)
    },
    mergeItems(rows, group = false) {
      if (this.itemsByVariation) {
        this.setGlobalAction({ type: 'itemVariationsMerge', preventMultiple: true, action: { itemVariationIds: rows.map(i => i.item_distributor_id) } })
      } else {
        this.setGlobalAction({ type: 'itemsMerge', preventMultiple: true, action: { group, itemIds: rows.map(i => i.item_id) } })
      }
      this.deselectAll()
    },
    categorizeItems(rows) {
      if (rows.length && this.$refs.confirmDialog) {
        this.$refs.confirmDialog.confirm({
          callback: () => {
            this.wiskOverlay = true
            api.categorizeItems({
              items: rows.map(i => ({ item_id: i.item_id, title: i.title })),
              categories: this.categories.map(c => ({ id: c.id, title: c.title }))
            }).then(res => api.updateBottlesCategoriesBatch({
                elements: res.data
            })).finally(() => {
              this.notify({
                message: this.translations.txtWiskItemsCategorizeDescription,
                type: 'success'
              })
            }).finally(() => {
              this.wiskOverlay = false
            })
          },
          message: this.translations.confirmCategorizeItems,
          title: this.translations.confirmCategorizeItemsTitle
        })
      }
    },
    exportExcel(p) {
      //called from outside
      if (this.$refs.wiskGrid && this.$refs.wiskGrid.exportExcel) {
        this.$refs.wiskGrid.exportExcel(p)
      }
    },
    selectRowsByItemId(itemIds) {
      if (this.gridApi && Array.isArray(itemIds)) {
        this.gridApi.forEachLeafNode(node => {
          node.setSelected(false)

          let itemId = node.data && node.data.item_id
          if (itemId && itemIds.includes(itemId)) {
            node.setSelected(true)
          }
        })
      }
    },
    updateBottles(operation, rows) {
      if (operation) {
        if (this.$refs.confirmDialog) {
          this.$refs.confirmDialog.confirm({
            callback: () => {
              this.calUpdateBatch(operation, rows)
            },
            message: this.translations.confirmItemsGridFloatingActionText,
            title: this.translations.confirmItemsGridFloatingActionTitle
          })
        }
      }
    },
    // operation comes from the order guide select: { type: 'order_guide_list_id', value: <list id or list> }
    updateOrderGuideList(value, operation, rows) {
      let orderGuideListId = operation?.value?.id || operation?.value
      if (!orderGuideListId) {
        return
      }
      let orderGuideOperation = { type: 'order_guide', value, order_guide_list_id: orderGuideListId }

      if (this.variantIdAvailable) {
        this.updateItemVariants(orderGuideOperation, rows)
      } else {
        this.updateBottles(orderGuideOperation, rows)
      }
    },
    updateItemVariants(operation, rows) {
      if (operation) {
        if (this.$refs.confirmDialog) {
          this.$refs.confirmDialog.confirm({
            callback: () => {
              let all = []
              rows.forEach(row => {
                row && all.push(this.setItemVariant({ id: row.item_distributor_id, operation }))
              })
              Promise.all(all).finally(() => {
                this.deselectAll()
              })
            },
            message: this.translations.confirmItemsGridFloatingActionText,
            title: this.translations.confirmItemsGridFloatingActionTitle
          })
        }
      }
    },
    updateBottlesQuick(operation, rows) {
      if (operation && rows && rows.length) {
        api.updateBottlesBatch({ item_ids: rows.map(item => item && item.item_id), operation })
      }
    },
    calUpdateBatch(operation, items = []) {
      this.loadingOverlay = true
      if (items && items.length) {
        api
          .updateBottlesBatch({ item_ids: items.map(item => item && item.item_id), operation })
          .then(failed => {
            let other = failed.filter(f => f && f.type === 'other')
            other.forEach(o => {
              this.notify({
                message: get(o, 'value.reason'),
                type: 'error'
              })
            })

            failed = failed.filter(f => f && f.type !== 'other')
            if (failed && failed.length) {
              this.setGlobalAction({
                type: 'itemOperationErrorHandler',
                action: {
                  items: failed,
                  operation,
                  retry: this.calUpdateBatch,
                  complete: () => {
                    this.selectRowsByItemId([])
                  }
                }
              })
            }
            this.selectRowsByItemId(failed.map(f => f.value.item_id))
            this.loadingOverlay = false
          })
          .catch(() => {
            this.loadingOverlay = false
          })
        this.deselectAll()
      }
    },
    setVisibleRows(rows) {
      this.visibleRows = rows
    },
    deselectAll() {
      this.gridApi && this.gridApi.deselectAll()
    },
    downloadSpreadsheet() {
      api.downloadBlob('bottles/export', 'Venue_Items.xlsx', { item_ids: this.visibleRows.map(row => row.item_id) })
    },
    emailPDF() {
      api.emailPdf({ operation: { type: 'bottles' } })
    },
    saveItemVariant({ value, id, type, previousValue }) {
      if (id) {
        let operation = { type, value, from: previousValue }
        // without a list id the API uses the default order guide
        if (type === 'order_guide' && this.orderGuideColumnList) {
          operation.order_guide_list_id = this.orderGuideColumnList.id
        }
        this.setItemVariant({ id, operation })
      }
    },
    save({ value, id, type, previousValue }) {
      if (type === 'archive') {
        api
          .updateBottlesBatch({
            item_ids: [id],
            operation: { type, value }
          })
          .then(failed => {
            let other = failed.filter(f => f && f.type === 'other')
            other.forEach(o => {
              this.notify({
                message: get(o, 'value.reason'),
                type: 'error'
              })
            })

            failed = failed.filter(f => f && f.type !== 'other')
            if (failed && failed.length) {
              this.setGlobalAction({
                type: 'itemOperationErrorHandler',
                action: {
                  items: failed,
                  operation: { type, value },
                  retry: (operation, items) => {
                    this.save({ value: operation.value, id: items[0].item_id, type: operation.type })
                  }
                }
              })
            }
          })
      } else {
        this.updateBottle({
          id,
          operation: { type, value, from: previousValue }
        })
      }
    },
    getGetters() {
      return this.$store.getters
    },
    getDistributorsById() {
      return this.distributorsById || {}
    },
    getOwnBatchWithoutRealTimeStock(item) {
      return ownBatchWithoutRealTimeStock(item, this.$store.state)
    },
    forceRefreshAll(params) {
      clearTimeout(this.forceRefreshAllTimeoutId)
      this.forceRefreshAllTimeoutId = setTimeout(() => {
        if (!this.unMounted && this.gridApi) {
          this.gridApi.refreshCells(params)
        }
      }, 300)
    },
    createColumnDefs() {
      clearTimeout(this.createColomDefsTimeoutId)

      this.createColomDefsTimeoutId = setTimeout(() => {
        let itemsGridColDefs = getItemsGridColdefs({
          translations: this.translations,
          itemsByVariation: this.itemsByVariation,
          allergensById: this.allergensById,
          currentPermissionsByType: this.currentPermissionsByType,
          translate: this.translations.translate,
          saveItemVariant: this.saveItemVariant,
          orderGuideColumnListTitle: this.orderGuideColumnListTitle,
          setGlobalAction: this.setGlobalAction,
          hasPos: this.hasPos,
          preventCustomFieldsAsColumns: this.preventCustomFieldsAsColumns,
          save: this.save,
          locationsById: this.locationsById,
          getGetters: this.getGetters,
          getDistributorsById: this.getDistributorsById,
          getOwnBatchWithoutRealTimeStock: this.getOwnBatchWithoutRealTimeStock,
          customFields: (this.customFieldsByTarget && this.customFieldsByTarget.item) || []
        })
        this.columnDefs = []

        if (itemsGridColDefs.distributor) {
          itemsGridColDefs.distributor.cellEditorParams.addNewItem = {
            action: (id, searchQuery, value, callbackItemInjected) => {
              this.setGlobalAction({
                type: 'distributorEdit',
                action: {
                  id: 0,
                  title: searchQuery,
                  onChange: distributor => {
                    callbackItemInjected(distributor)
                  }
                }
              })
            },
            label: this.translations.txtGenericNewDistributor
          }
        }

        if (itemsGridColDefs.category && this.currentPermissionsByType.family_category_view) {
          itemsGridColDefs.category.cellEditorParams.addNewItem = {
            action: (id, searchQuery, value, callbackItemInjected) => {
              this.setGlobalAction({
                type: 'categoryEdit',
                action: {
                  id: 0,
                  title: searchQuery,
                  onChange: category => {
                    callbackItemInjected(category)
                  }
                }
              })
            },
            label: this.translations.txtGenericCreateCategory
          }
        }

        Object.keys({ ...this.columns }).forEach(key => {
          //must detach it from props in order to change it
          let column = merge({}, itemsGridColDefs[key] || {}, this.columns[key] || {})

          if (key === 'dropdownMenu') {
            let temp = this.columns[key].valueGetter

            if (this.columns[key].forceOverrideValuegetter) {
              column.valueGetter = temp
            } else {
              ///special case: merge functions here
              column.valueGetter = params => {
                let result = (temp && temp(params)) || {},
                  items = result.items || []
                if (!params.node.group) {
                  if (this.currentPermissionsByType.item_archive) {
                    items.push({
                      onClick: item => {
                        if (this.itemsByVariation) {
                          this.saveItemVariant({ value: true, id: item.item_distributor_id, type: 'archive', previousValue: false })
                        } else {
                          this.save({ value: true, id: item.item_id, type: 'archive', previousValue: false })
                        }
                      },
                      label: this.translations.txtGenericArchive,
                      value: params.data,
                      hide: params.data?.archived || params.data?.variant?.archived,
                      cssClass: 'text-danger'
                    })
                    items.push({
                      onClick: item => {
                        if (this.itemsByVariation) {
                          this.saveItemVariant({ value: false, id: item.item_distributor_id, type: 'archive', previousValue: true })
                        } else {
                          this.save({ value: false, id: item.item_id, type: 'archive', previousValue: true })
                        }
                      },
                      label: this.translations.txtGenericRestore,
                      value: params.data,
                      cssClass: 'text-success',
                      hide: this.itemsByVariation ? !params?.data?.variant?.archived || params?.data?.variant?.archived_by_merge : !params?.data?.archived || params?.data?.archived_by_merge
                    })
                    items.unshift({
                      onClick: item => {
                        this.setGlobalAction({ type: 'itemSwap', action: { itemId: item.item_id } })
                      },
                      label: this.translations.txtItemSwap,
                      value: params.data,
                      hide: !params?.data?.archived || params?.data?.archived_by_merge
                    })
                  }

                  if (this.currentPermissionsByType.item_manage) {
                    items.unshift({
                      onClick: item => {
                        if (this.itemsByVariation) {
                          api.cloneItemVariant(item.item_distributor_id)
                        } else {
                          api.cloneItem(item.item_id)
                        }
                      },
                      planFeature: 'items',
                      planFeatureLimitedItemsCount: this.activeBottles.length,
                      value: params.data,
                      label: this.translations.txtGenericClone,
                      hide: params?.data?.archived_by_merge
                    })
                  }
                }

                return {
                  group: params.node.group,
                  items
                }
              }
            }
          }

          this.columnDefs.push(markRaw(column))
        })

        Object.keys({ ...itemsGridColDefs }).forEach(key => {
          if (key && key.startsWith('custom-field')) {
            this.columnDefs.push(markRaw(merge({}, itemsGridColDefs[key] || {})))
          }
        })

        if (this.columns && !this.columns.wiskId) {
          this.columnDefs.push(markRaw(merge({}, itemsGridColDefs.wiskId || {})))
        }
      }, 100)
    },
    getRowHeight(params) {
      let hidden = (params.data && params.data.wiskRowHidden) || (params.node.group && params.node.key === 'hidden')
      return (hidden && 1) || (params.node.group ? 30 : 50)
    },
    getRowClass(params) {
      let hidden = (params.data && params.data.wiskRowHidden) || (params.node.group && params.node.key === 'hidden')
      return (hidden && 'invisible') || ''
    },
    prepareLocalItems(caller, timeout = 3000) {
      if (caller && this.debug) {
        console.log('+++ --- +++ WiskItemsGrid prepareLocalItems caller', caller)
        console.log('this.reactToChangesFast', this.reactToChangesFast)
      }
      clearTimeout(this.prepareLocalItemsTimeoutId)
      this.prepareLocalItemsTimeoutId = setTimeout(
        () => {
          if (caller && this.debug) {
            console.log('+++ --- +++ in timeout WiskItemsGrid prepareLocalItems caller', caller)
          }

          if (this.debug) {
            console.time('prepareLocalItems')
          }

          this.localItems = this.items.filter(i => i).map(item => {

            if (item && !item.wiskRowHidden && !item.online) {
              item.item_distributor_ids = item.item_distributor_ids || []

              item.item_distributor_ids.forEach(id => {
                if (id && !this.itemVariantsById[id] && !this.hasCentralStockroom) {
                  Sentry.withScope(scope => {
                    scope.setExtra('item_id', item.item_id)
                    scope.setExtra('variant id', id)
                    scope.setExtra('item.item_distributor_ids', item.item_distributor_ids)
                    scope.setExtra('this.itemVariantsById[id]', this.itemVariantsById[id])
                    scope.setExtra('browser address', window.location.href)
                    Sentry.captureException(new Error('Variation missing for item ' + item.title))
                  })
                }
              })

              let variants = item.variants || item.item_distributor_ids.map(id => id && this.itemVariantsById[id]),
                defaultVariant = variants.find(v => v && v.is_default),
                variant = item.item_distributor_id ? variants.find(v => v && item.item_distributor_id === v.id) : defaultVariant,
                price = item.item_distributor_id ? this.itemVariantPricesById[item.item_distributor_id] : this.itemPricesById[item.item_id],
                distributor = this.distributorsById[variant?.distributor_id] || {}

              if (this.centralStockroom) {
                distributor = this.distributorLinkedToCentralStockroom || distributor
              }

              if (!variant) {
                if (item.item_id || item.item_distributor_id) {
                  let description = 'Default variation not found for item ' + item.item_id
                  if (item.item_distributor_id) {
                    description = 'Variation not found for variation id ' + item.item_distributor_id
                  }
                  // api.postErrorInfo({ description, item_distributor_id: item.item_distributor_id, item_id: item.item_id, item_distributor_ids: item.item_distributor_ids })

                  console.warn(description, variants)
                }
                variant = defaultVariant || {}
              }

              // Order of properties are important here, ...item can replace category and family properties if necessary
              let computed = {
                category: this.categoriesById[item.category_id],
                family: this.familiesById[this.categoriesById[item.category_id] && this.categoriesById[item.category_id].family_id],
                ...item,
                weights: variant.weights,
                empty_weights: variant.empty_weights,
                barcodes: variant.barcodes,
                measurement: variant.measurement,
                case_size: variant.case_size,
                order_format: variant.order_format,
                fee: variant.fee,
                distributor_id: distributor.id,
                distributor_code: variant.item_code,
                distributor_title: distributor.title,
                distributor,
                country: this.countriesByCode[item.country_code],
                extra: {
                  stockPredictionFromSales: this.bottleStockPredictionsFromSalesById[item.item_id],
                  cart: this.shoppingCartByItemId[item.item_id] || { item_id: item.item_id, units: 0 },
                  stockPrediction: this.bottlesStockPredictionsById[item.item_id],
                  algo: this.bottleAlgosById[item.item_id]
                },
                defaultVariant,
                variants,
                price,
                par_level: get(this.parLevelsById, `${item.item_id}.par_level`),
                parLevelType: get(this.parLevelsById, `${item.item_id}.type`),
                // the order guide the checkbox column works on (see orderGuideColumnList)
                order_guide_item: this.orderGuideColumnVariantIdsMap[get(item, 'item_distributor_id', defaultVariant?.id)],
                order_guide_lists: this.orderGuideListTitlesByVariantId[get(item, 'item_distributor_id', defaultVariant?.id)] || ''
              }

              if (!computed.price && item.price) {
                if (typeof item.price === 'object' && item.price) {
                  computed.price = item.price
                }
                if (typeof item.price === 'number') {
                  computed.price = { price: { type: 'unit', value: item.price }, price_per_unit: item.price }
                }
              }

              if (item.item_distributor_id) {
                computed.variant = this.itemVariantsById[item.item_distributor_id]
              }
              return markRaw(computed)
            }
            return item
          })
          if (this.debug) {
            console.timeEnd('prepareLocalItems')
          }
        },
        this.reactToChangesFast ? 0 : timeout
      )
    },
    copyItemToVenue() {
      api.copyDataToVenue({
        'origin_venue_id': this.venue.id,
        'destination_venue_ids': this.venuesToCopyItem.map(v => v.id),
        'data_filter': {
          type: 'filtered_items',
          item_ids: this.copyToVenueRows.map(row => row.item_id)
        },
        'only_match_based_on_ids': this.onlyMatchBasedOnIds,
        'keep_distributors_on_items': this.keepDistributorsOnItems
      }).then(() => {
        this.notify({
          message: this.translations.confirmCopyDataToVenue,
          type: 'success'
        })
      })
    },
    filterSystemViews(view) {
      if (view.id === 'c1a95dfd-3efe-4aee-b64a-d9e6c70c2625' || view.id === 'fb7d4d63-b1bf-4aa2-bf18-5f1ff732dbed') {
        return this.venue.accounting_enabled
      }
      return true
    }
  },
  created() {
    this.gridOptions = merge(
      {
        rowClassRules: {
          'danger-row': 'data?.archived || data?.variant?.archived'
        },
        getRowClass: this.getRowClass,
        getRowHeight: this.getRowHeight,
        rowSelection: {
          isRowSelectable: rowNode => !rowNode?.data?.wiskRowHidden && !rowNode?.data?.archived_by_merge
        }
      },
      this.options
    )
  },
  beforeUnmount() {
    this.unMounted = true
    this.gridApi = null

    clearTimeout(this.createColomDefsTimeoutId)
    clearTimeout(this.forceRefreshAllTimeoutId)
    clearTimeout(this.prepareLocalItemsTimeoutId)
  },
  watch: {
    loading: {
      immediate: true,
      handler(loading) {
        this.loadingOverlay = loading
      }
    },
    items: {
      immediate: true,
      handler() {
        this.prepareLocalItems('items', 500)
      }
    },
    itemPricesById() {
      if (this.columns && (this.columns.costUnit || this.columns.inventoryValue)) {
        this.prepareLocalItems('itemPricesById')
      }
    },
    distributorsById() {
      this.prepareLocalItems('distributorsById')
      this.forceRefreshAll({ force: true, columns: ['distributor'] })
    },
    bottleAlgosById() {
      this.prepareLocalItems('bottleAlgosById')
    },
    bottleStockPredictionsFromSalesById() {
      console.log('this.columns.parSuggestedSales', this.columns.parSuggestedSales)
      if (this.columns && this.columns.parSuggestedSales) {
        this.prepareLocalItems('bottleStockPredictionsFromSalesById')
      }
    },
    categoriesById() {
      if (this.columns && this.columns.category) {
        this.prepareLocalItems('categoriesById')
      }
    },
    familiesById() {
      if (this.columns && this.columns.family) {
        this.prepareLocalItems('familiesById')
      }
    },
    bottlesStockPredictionsById() {
      if (this.columns && this.columns.parSuggested) {
        this.prepareLocalItems('bottlesStockPredictionsById')
      }
    },
    parLevelsById() {
      if (this.columns && (this.columns.parSuggested || this.columns.parSuggestedSales || this.columns.par)) {
        this.prepareLocalItems('parLevelsById')
      }
    },
    orderGuideVariantIdsMap() {
      if (this.columns && (this.columns.orderGuide || this.columns.orderGuideLists)) {
        this.prepareLocalItems('orderGuideVariantIdsMap')
      }
    },
    activeOrderGuideLists() {
      if (this.columns && (this.columns.orderGuide || this.columns.orderGuideLists)) {
        this.prepareLocalItems('activeOrderGuideLists')
      }
    },
    customFilter() {
      this.deselectAll()
    },
    orderGuideColumnVariantIdsMap() {
      if (this.columns && this.columns.orderGuide) {
        this.prepareLocalItems('orderGuideColumnVariantIdsMap')
      }
    },
    orderGuideColumnListTitle() {
      if (this.columns && this.columns.orderGuide) {
        this.createColumnDefs('orderGuideColumnListTitle')
      }
    },
    columns() {
      console.log('this.columns', this.columns)
      if (this.columns) {
        this.createColumnDefs('columns')
      }
    },
    activeFamilies() {
      this.forceRefreshAll({ force: true, columns: ['category'] })
    },
    priceFee() {
      this.createColumnDefs('priceFee')
    },
    priceRetail() {
      this.createColumnDefs('priceRetail')
    },
    // bottles() {
    //   this.createColumnDefs('bottles')
    // },
    itemIdFilter: {
      immediate: true,
      handler() {
        if (this.itemIdFilter) {
          this.localItemIdFilter = this.itemIdFilter
        }
      }
    },
    'customFieldsByTarget.item': {
      handler() {
        this.createColumnDefs('customFieldsByTarget.item')
      },
      immediate: true
    },
    user: {
      handler() {
        this.configAvailable = !!(this.user && this.user.id)
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.ag-theme-balham .ag-cell-inline-editing {
  height: 35px;
}

#txt-items-grid-search-box {
  max-width: 600px;
}

.ag-root {
  .image-cell {
    overflow: visible;
    cursor: pointer;
    padding: 0 !important;
    border: 0;
    text-align: center;
  }
}

.wisk-header {
  padding-left: 10px !important;
}

//.multiselect__tags {
//min-height: 45px !important;
//padding: 12px 40px 0 8px !important;
//}
.multiselect__select {

  padding-top: 12px !important;
}

.measurement-component-container-in-grid-actions {
  >.row {
    max-height: 33px;
  }
}
</style>
